/* eslint-disable react-hooks/rules-of-hooks
 -- Disabled to set CI to fail on this issue on new files, PR #5195 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { Editor as TipTapEditor } from "@tiptap/react";
import classNames from "classnames";
import { useRouteMatch } from "react-router-dom";
import { Carousel } from "@/react/components/Spaces/ImageSpace/FeedView/Carousel";
import { CommentsSection } from "@circle-react/components/PostsV3/PostViewListItem/CommentsSection";
import { EngagementActions } from "@circle-react/components/PostsV3/PostViewListItem/EngagementActions";
import { PinnedToTopIcon } from "@circle-react/components/PostsV3/PostViewListItem/PinnedToTopIcon";
import { useCurrentPostHelpers } from "@circle-react/components/PostsV3/useCurrentPostHelpers";
import {
  useCurrentPostContext,
  usePunditUserContext,
} from "@circle-react/contexts";
import { isBasicPost, isImagePost } from "@circle-react/helpers/postHelpers";
import { useSmScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import type { TrixEditorRefObject as TrixEditor } from "@circle-react-shared/TrixEditor";
import { isTrixEditorRefObject } from "@circle-react-shared/TrixEditor";
import { MinimalPostBody } from "./MinimalPostBody";
import { PostBody } from "./PostBody";
import { PostContentWrapper } from "./PostContentWrapper";
import { PostCoverImage } from "./PostCoverImage";
import { PostHeader } from "./PostHeader";
import { PostSummary } from "./PostSummary";

export interface MainContentProps {
  autofocusReplyForm?: boolean;
  className?: string;
  defaultShowComment?: boolean;
  hideAuthorHeadline?: boolean;
  hideBookmarkButton?: boolean;
  hidePinnedToTopIcon?: boolean;
  hideShareLink?: boolean;
  isRenderedOutsideTheSpace?: boolean;
  metaInfoWrapperClassName?: string;
  onPostChange: (post: any, options?: any) => void;
  onPostDestroy: (post: any) => void;
  pageSupportsContentTruncation?: boolean;
  refetchPosts?: () => void;
  scrollableTarget?: React.RefObject<HTMLElement>;
  shouldForceSamePageInteractions?: boolean;
  shouldOpenCommentPage?: boolean;
  tagsLimit?: number;
  tagsShowMoreLabelVariant?: "words" | "numbers";
}

export const MainContent = ({
  autofocusReplyForm = false,
  className,
  defaultShowComment = false,
  hideAuthorHeadline = false,
  hideBookmarkButton = false,
  hidePinnedToTopIcon = false,
  hideShareLink = false,
  isRenderedOutsideTheSpace = false,
  metaInfoWrapperClassName,
  onPostChange,
  onPostDestroy,
  pageSupportsContentTruncation = true,
  refetchPosts,
  shouldOpenCommentPage = false,
  tagsLimit = 3,
  tagsShowMoreLabelVariant = "numbers",
  shouldForceSamePageInteractions = false,
  scrollableTarget,
}: MainContentProps) => {
  const commentsFormRef = useRef<TrixEditor | TipTapEditor>(null);
  const { record: post, usedIn } = useCurrentPostContext();
  const { isPostBodyEmpty, showPostActions: shouldShowPostActions } =
    useCurrentPostHelpers();
  const [shouldShowComment, setShouldShowComment] =
    useState(defaultShowComment);
  const [shouldAutofocusReplyForm, setShouldAutofocusReplyForm] =
    useState(autofocusReplyForm);
  const isSmScreen = useSmScreenMediaQuery();

  const {
    name,
    is_truncation_disabled,
    is_comments_enabled,
    flagged_for_approval_at,
    hide_meta_info,
    pinned_at_top_of_space,
    post_details_loaded,
  } = post;

  const isShowPostPage = useRouteMatch("/c/:spaceSlug/:postSlug")?.isExact;
  const shouldRenderMinimalTipTapView =
    isBasicPost(post) &&
    !isShowPostPage &&
    !is_truncation_disabled &&
    !shouldForceSamePageInteractions;

  const isPostTruncationEnabled = !is_truncation_disabled;
  const { currentUser } = usePunditUserContext();

  const { isSharedPostView, isSharedPostWithTeaserLayout } =
    useCurrentPostHelpers();

  const hasContentTruncation =
    (pageSupportsContentTruncation && isPostTruncationEnabled) ||
    isSharedPostWithTeaserLayout;

  const shouldHideSummary = hasContentTruncation;

  const isImageOrBasicPost = isImagePost(post) || isBasicPost(post);

  const isUsedInFullPostModal = usedIn === "full-post-modal";

  const shouldNotRenderHeader =
    !shouldShowPostActions && usedIn === "full-post-modal" && hide_meta_info;

  if (!post || !post.id) {
    return null;
  }

  const focusCommentsForm = useCallback(
    () =>
      isTrixEditorRefObject(commentsFormRef.current) &&
      commentsFormRef.current.focus(),
    [commentsFormRef],
  );

  const toggleComment = () => {
    setShouldAutofocusReplyForm(false);
    setShouldShowComment(prev => !prev);
  };

  useEffect(() => {
    if (shouldAutofocusReplyForm) {
      focusCommentsForm();
    }
  }, [focusCommentsForm]);

  const containerClassName = classNames(
    "post post--parent bg-primary border-primary rounded-none border first:mt-5 first:md:mt-0 md:rounded-2xl",
    {
      "post--shared": isSharedPostView,
      "post--shared__truncated": isSharedPostWithTeaserLayout && !currentUser,
      "is-truncation-disabled": !hasContentTruncation,
      "flagged-for-approval": flagged_for_approval_at,
      "post-name--empty": !name,
      "post-meta--hidden": hide_meta_info,
      "is-pinned": pinned_at_top_of_space,
      "!shadow-none !rounded-none !border-0": isUsedInFullPostModal,
      "!mb-5 md:!mb-6": !isUsedInFullPostModal,
    },
    className,
  );

  const onChange = (updates = {}, options = {}) => {
    onPostChange({ ...post, ...updates }, options);
  };

  const showComments = useCallback(() => {
    setShouldShowComment(true);
    setShouldAutofocusReplyForm(true);

    // Focus the reply box if its already open
    if (commentsFormRef.current) {
      if (!isTrixEditorRefObject(commentsFormRef.current)) {
        commentsFormRef.current.commands?.focus?.("end");
      } else if (isTrixEditorRefObject(commentsFormRef.current)) {
        commentsFormRef.current.focus();
      }
    }
  }, []);

  return (
    <div className={containerClassName} data-testid="post-container">
      {!hidePinnedToTopIcon && isSmScreen && isImageOrBasicPost && (
        <PinnedToTopIcon post={post} />
      )}
      <PostCoverImage post={post} />
      {!shouldHideSummary && (
        <div
          className={classNames(
            "overflow-hidden bg-transparent md:rounded-t-2xl",
            {
              "rounded-t-none md:rounded-t-none":
                isUsedInFullPostModal || Boolean(post.cover_image_url),
            },
          )}
        >
          <PostSummary post={post} />
        </div>
      )}
      <div className="post__post">
        <PostContentWrapper>
          <div
            className={classNames({
              "mb-5": !isPostBodyEmpty,
              "px-5 md:px-6": isImagePost(post),
              hidden: shouldNotRenderHeader,
            })}
          >
            <PostHeader
              post={post}
              isRenderedOutsideTheSpace={isRenderedOutsideTheSpace}
              onChange={onChange}
              refetchPosts={refetchPosts}
              onPostDestroy={onPostDestroy}
              hideShareLink={hideShareLink}
              hidePinnedToTopIcon={hidePinnedToTopIcon}
              hideBookmarkButton={hideBookmarkButton}
              hideSummarizeButton={shouldHideSummary}
              tagsLimit={isSmScreen ? 1 : tagsLimit}
              tagsShowMoreLabelVariant={tagsShowMoreLabelVariant}
              metaInfoWrapperClassName={metaInfoWrapperClassName}
              hideAuthorHeadline={hideAuthorHeadline}
            />
          </div>
          {isImagePost(post) && usedIn !== "full-post-modal" && (
            <Carousel
              onPostChange={onPostChange}
              onPostDestroy={onPostDestroy}
            />
          )}
          {shouldRenderMinimalTipTapView ? (
            <MinimalPostBody />
          ) : (
            <PostBody enableTruncation={hasContentTruncation} />
          )}
          {isSharedPostWithTeaserLayout && (
            <div className="to-primary absolute bottom-0 left-0 z-10 h-1/2 w-full bg-gradient-to-b from-transparent" />
          )}
        </PostContentWrapper>
      </div>
      <div className="mt-auto">
        <EngagementActions
          toggleComment={toggleComment}
          showCommentAndFocusCommentForm={showComments}
          shouldOpenCommentPage={shouldOpenCommentPage}
          shouldForceSamePageInteractions={shouldForceSamePageInteractions}
        />
      </div>
      {post_details_loaded && is_comments_enabled && shouldShowComment && (
        <CommentsSection
          commentsFormRef={commentsFormRef}
          autofocusReplyForm={shouldAutofocusReplyForm}
          scrollableTarget={scrollableTarget}
        />
      )}
    </div>
  );
};
